import { z, ZodIssueCode, type ZodErrorMap } from 'zod';

// =============================================================================
// Global Zod error map in Brazilian Portuguese.
// Imported as a side-effect from the package index, so any consumer
// (web, api) gets the localized defaults automatically. Schemas can still
// override individual messages via .min(N, 'mensagem').
// =============================================================================

const ptBrErrorMap: ZodErrorMap = (issue, ctx) => {
  let message: string;

  switch (issue.code) {
    case ZodIssueCode.invalid_type: {
      if (issue.received === 'undefined' || issue.received === 'null') {
        message = 'Campo obrigatório';
      } else {
        message = `Tipo inválido (esperado ${friendlyType(issue.expected)})`;
      }
      break;
    }

    case ZodIssueCode.invalid_string: {
      if (issue.validation === 'email') message = 'E-mail inválido';
      else if (issue.validation === 'url') message = 'URL inválida';
      else if (issue.validation === 'uuid') message = 'UUID inválido';
      else if (issue.validation === 'regex') message = 'Formato inválido';
      else if (typeof issue.validation === 'object' && 'startsWith' in issue.validation) {
        message = `Deve começar com "${issue.validation.startsWith}"`;
      } else if (typeof issue.validation === 'object' && 'endsWith' in issue.validation) {
        message = `Deve terminar com "${issue.validation.endsWith}"`;
      } else {
        message = 'Texto inválido';
      }
      break;
    }

    case ZodIssueCode.too_small: {
      const min = issue.minimum;
      if (issue.type === 'string') {
        message =
          min === 1
            ? 'Campo obrigatório'
            : `Mínimo de ${min} caracteres`;
      } else if (issue.type === 'number') {
        message = issue.inclusive
          ? `Deve ser maior ou igual a ${min}`
          : `Deve ser maior que ${min}`;
      } else if (issue.type === 'array') {
        message = `Selecione ao menos ${min} ${min === 1 ? 'item' : 'itens'}`;
      } else if (issue.type === 'date') {
        message = `Data deve ser após ${new Date(Number(min)).toLocaleDateString('pt-BR')}`;
      } else {
        message = 'Valor abaixo do mínimo';
      }
      break;
    }

    case ZodIssueCode.too_big: {
      const max = issue.maximum;
      if (issue.type === 'string') {
        message = `Máximo de ${max} caracteres`;
      } else if (issue.type === 'number') {
        message = issue.inclusive
          ? `Deve ser menor ou igual a ${max}`
          : `Deve ser menor que ${max}`;
      } else if (issue.type === 'array') {
        message = `Máximo de ${max} ${max === 1 ? 'item' : 'itens'}`;
      } else if (issue.type === 'date') {
        message = `Data deve ser antes de ${new Date(Number(max)).toLocaleDateString('pt-BR')}`;
      } else {
        message = 'Valor acima do máximo';
      }
      break;
    }

    case ZodIssueCode.invalid_enum_value: {
      message = `Opção inválida. Valores aceitos: ${issue.options.join(', ')}`;
      break;
    }

    case ZodIssueCode.invalid_date: {
      message = 'Data inválida';
      break;
    }

    case ZodIssueCode.invalid_literal: {
      message = `Valor esperado: ${JSON.stringify(issue.expected)}`;
      break;
    }

    case ZodIssueCode.unrecognized_keys: {
      message = `Campo(s) não reconhecido(s): ${issue.keys.join(', ')}`;
      break;
    }

    case ZodIssueCode.invalid_union: {
      message = 'Nenhuma das opções aceitas é válida';
      break;
    }

    case ZodIssueCode.custom: {
      message = issue.message ?? 'Valor inválido';
      break;
    }

    default:
      message = ctx.defaultError;
  }

  return { message };
};

function friendlyType(t: string): string {
  switch (t) {
    case 'string': return 'texto';
    case 'number': return 'número';
    case 'boolean': return 'sim/não';
    case 'date': return 'data';
    case 'array': return 'lista';
    case 'object': return 'objeto';
    default: return t;
  }
}

z.setErrorMap(ptBrErrorMap);
